import React, { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Building2, Eye, Pencil, ShieldCheck } from "lucide-react";

import {
  API_EMPLOYEE_INSURANCE,
  API_EMPLOYEE_INSURANCE_EXISTING,
  API_GET_EMPLOYEE_FILE,
  API_HOSPITAL_LIST,
} from "../../../../config/Endpoints.js";
import DocumentViewer from "../../../../components/DocumentViewer.jsx";
import { getAuthToken } from "../../../../utils/Storage.js";
import { formatCalendarDateDisplay } from "../../../../utils/helpers.js";
import { Card } from "../../shared/card/card.tsx";
import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  FileField,
  RadioGroup,
  Spinner,
  TextField,
} from "../../ui/index.ts";
import {
  getApi,
  postFormDataMultipart,
  putFormDataMultipart,
} from "../../../utils/api.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { isInsuranceEligibleLocation } from "../../../utils/insurance-eligibility.ts";

import styles from "./employee-insurance-tab.module.css";

const PROVIDER_NAME = "HDFC ERGO";
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
const INSURANCE_NUMBER_PATTERN = /^[A-Z]{2}\d{10}-\d{2}[A-Z]$/;
const DEFAULT_INSURANCE_FROM = "2025-11-17";
const DEFAULT_INSURANCE_TO = "2026-11-16";
const INSURANCE_VALID_TO_MAX_DATE = new Date(
  new Date().getFullYear() + 50,
  11,
  31,
);

type Identifier = number | string;

type InsuranceEntryData = {
  name?: string | null;
  dob?: string | null;
  gender?: string | null;
  spouseName?: string | null;
  spouseDob?: string | null;
  childName?: string | null;
  childDob?: string | null;
  childGender?: string | null;
  insuranceNumber?: string | null;
  insuranceFrom?: string | null;
  insuranceTo?: string | null;
  insuranceSoftCopy?: string | null;
};

type FamilyInsuranceData = {
  spouseInsurance?: InsuranceEntryData | null;
  childInsurance?: InsuranceEntryData | null;
  spouseInsuranceNumber?: string | null;
  spouseInsuranceFrom?: string | null;
  spouseInsuranceTo?: string | null;
  spouseName?: string | null;
  spouseDob?: string | null;
  childInsuranceNumber?: string | null;
  childInsuranceFrom?: string | null;
  childInsuranceTo?: string | null;
  childName?: string | null;
  childDob?: string | null;
  childGender?: string | null;
};

export type EmployeeInsuranceDetails = InsuranceEntryData & {
  id?: Identifier | null;
  insuranceId?: Identifier | null;
  insuranceProviderName?: string | null;
  childStatus?: boolean | null;
  maritalStatus?: string | null;
  location?: string | null;
  employeeType?: string | null;
  familyInsuranceDetails?: FamilyInsuranceData | null;
};

type EmployeeInsuranceTabProps = {
  employeeId?: Identifier | null;
  maritalStatus?: string | null;
  insuranceDetails?: EmployeeInsuranceDetails | null;
  loading?: boolean;
  onInsuranceUpdated?: (details: EmployeeInsuranceDetails) => void;
  onInsuranceSaved?: () => void;
};

type InsuranceEntryForm = {
  name: string;
  dob: string;
  gender: string;
  number: string;
  from: string;
  to: string;
  file: File | null;
};

type InsuranceForm = {
  self: InsuranceEntryForm;
  spouse: InsuranceEntryForm;
  child: InsuranceEntryForm;
};

type EntryErrors = Partial<Record<keyof InsuranceEntryForm, string>>;
type FormErrors = Partial<Record<keyof InsuranceForm, EntryErrors>>;
type HospitalDirectory = Record<string, string[]>;

const EMPTY_ENTRY: InsuranceEntryForm = {
  name: "",
  dob: "",
  gender: "",
  number: "",
  from: DEFAULT_INSURANCE_FROM,
  to: DEFAULT_INSURANCE_TO,
  file: null,
};

function asDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateOnly(date: Date | null): string {
  if (!date) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function normalizeNumber(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 16);
}

function normalizeName(value: string): string {
  return value.replace(/[^a-zA-Z\s]/g, "").replace(/\s{2,}/g, " ").slice(0, 100);
}

function normalizeGender(value?: string | null): string {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "MALE" || normalized === "M") return "M";
  if (normalized === "FEMALE" || normalized === "F") return "F";
  if (normalized === "OTHERS" || normalized === "OTHER" || normalized === "O") return "O";
  return "";
}

function genderLabel(value?: string | null): string {
  const normalized = normalizeGender(value);
  if (normalized === "M") return "Male";
  if (normalized === "F") return "Female";
  if (normalized === "O") return "Others";
  return "-";
}

function getFamilyEntry(
  family: FamilyInsuranceData,
  relation: "spouse" | "child",
): InsuranceEntryData {
  const nested = family[`${relation}Insurance`] ?? {};
  return {
    name:
      nested.name ??
      nested[`${relation}Name`] ??
      family[`${relation}Name`],
    dob:
      nested.dob ??
      nested[`${relation}Dob`] ??
      family[`${relation}Dob`],
    gender:
      relation === "child"
        ? nested.gender ?? nested.childGender ?? family.childGender
        : undefined,
    insuranceNumber:
      nested.insuranceNumber ?? family[`${relation}InsuranceNumber`],
    insuranceFrom: nested.insuranceFrom ?? family[`${relation}InsuranceFrom`],
    insuranceTo: nested.insuranceTo ?? family[`${relation}InsuranceTo`],
    insuranceSoftCopy: nested.insuranceSoftCopy,
  };
}

function toEntryForm(data?: InsuranceEntryData | null): InsuranceEntryForm {
  return {
    name: data?.name ?? "",
    dob: data?.dob?.slice(0, 10) ?? "",
    gender: normalizeGender(data?.gender),
    number: data?.insuranceNumber ?? "",
    from: data?.insuranceFrom?.slice(0, 10) || DEFAULT_INSURANCE_FROM,
    to: data?.insuranceTo?.slice(0, 10) || DEFAULT_INSURANCE_TO,
    file: null,
  };
}

function validateFile(file: File | null): string | undefined {
  if (!file) return undefined;
  if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
    return "Upload a PDF, JPG, JPEG, or PNG file";
  }
  if (file.size > MAX_FILE_SIZE) return "File size must not exceed 5 MB";
  return undefined;
}

function validateEntry(
  entry: InsuranceEntryForm,
  label: string,
  required: boolean,
  dependentType?: "spouse" | "child",
): EntryErrors {
  const hasValue = Boolean(entry.number || entry.from || entry.to || entry.file);

  const errors: EntryErrors = {};
  if (dependentType) {
    if (entry.name.trim() && !/^[a-zA-Z\s]+$/.test(entry.name.trim())) {
      errors.name = `${label} name should only contain alphabets`;
    }
    if (entry.dob) {
      const dob = asDate(entry.dob);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dob && dob > today) {
        errors.dob = `${label} date of birth cannot be in the future`;
      } else if (dependentType === "child" && dob) {
        const eighteenthBirthday = new Date(
          dob.getFullYear() + 18,
          dob.getMonth(),
          dob.getDate(),
        );
        if (eighteenthBirthday <= today) {
          errors.dob = "Child must be below 18 years of age";
        }
      }
    }
  }
  if (!required && !hasValue) return errors;
  if (!entry.number.trim()) {
    errors.number = label === "Self"
      ? "Insurance number is required"
      : `${label} insurance number is required`;
  } else if (!INSURANCE_NUMBER_PATTERN.test(entry.number.trim())) {
    errors.number = "Use format EC2426456447-01E";
  }
  if (!entry.from) {
    errors.from = label === "Self"
      ? "Valid from date is required"
      : `${label} valid from date is required`;
  }
  if (entry.from && entry.to && entry.to < entry.from) {
    errors.to = "Valid till date must be on or after valid from date";
  }
  const fileError = validateFile(entry.file);
  if (fileError) errors.file = fileError;
  return errors;
}

function appendEntry(
  payload: FormData,
  entry: InsuranceEntryForm,
  prefix: "" | "spouse" | "child",
): void {
  const key = (suffix: string) =>
    prefix ? `${prefix}Insurance${suffix}` : `insurance${suffix}`;
  if (entry.number) payload.append(key("Number"), entry.number);
  if (entry.from) payload.append(key("From"), entry.from);
  if (entry.to) payload.append(key("To"), entry.to);
  if (entry.file) payload.append(key("SoftCopy"), entry.file);
  if (prefix) {
    if (entry.name.trim()) payload.append(`${prefix}Name`, entry.name.trim());
    if (entry.dob) payload.append(`${prefix}Dob`, entry.dob);
    if (prefix === "child" && entry.gender) {
      payload.append("childGender", entry.gender);
    }
  }
}

function unwrapRecord(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function fileNameFromPath(path: string): string {
  return path.split(/[\\/]/).filter(Boolean).pop() ?? "Insurance document";
}

function contentTypeFromPath(path: string): string {
  const extension = path.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "application/pdf";
  if (extension === "png") return "image/png";
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  return "application/octet-stream";
}

type InsuranceEntryFieldsProps = {
  title: string;
  idPrefix: string;
  entry: InsuranceEntryForm;
  errors?: EntryErrors;
  required?: boolean;
  dependentType?: "spouse" | "child";
  disabled: boolean;
  onChange: (entry: InsuranceEntryForm) => void;
};

function InsuranceEntryFields({
  title,
  idPrefix,
  entry,
  errors = {},
  required = false,
  dependentType,
  disabled,
  onChange,
}: InsuranceEntryFieldsProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const childDobMin = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate() + 1,
  );

  return (
    <fieldset className={styles.entrySection} disabled={disabled}>
      <legend>{title}</legend>
      <div className={styles.formGrid}>
        {dependentType ? (
          <>
            <TextField
              id={`${idPrefix}-name`}
              label={`${title} Name`}
              maxLength={100}
              value={entry.name}
         placeholder={`Enter ${title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()} Name`}
              error={errors.name}
              onChange={(event) => onChange({ ...entry, name: normalizeName(event.target.value) })}
            />
            <DatePicker
              id={`${idPrefix}-dob`}
              label={`${title} Date of Birth`}
              selected={asDate(entry.dob)}
              onChange={(date) => onChange({ ...entry, dob: toDateOnly(date) })}
              minDate={dependentType === "child" ? childDobMin : undefined}
              maxDate={today}
              dateFormat="dd-MM-yyyy"
              placeholderText="dd-mm-yyyy"
              customInput={<DatePickerFieldTrigger />}
              error={errors.dob}
              disabled={disabled}
            />
            {dependentType === "child" ? (
              <RadioGroup
                id={`${idPrefix}-gender`}
                name={`${idPrefix}-gender`}
                label="Child Gender"
                options={[
                  { value: "M", label: "Male" },
                  { value: "F", label: "Female" },
                  { value: "O", label: "Others" },
                ]}
                value={entry.gender}
                onChange={(gender) => onChange({ ...entry, gender })}
                error={errors.gender}
                disabled={disabled}
              />
            ) : null}
          </>
        ) : null}
        <TextField
          id={`${idPrefix}-insurance-number`}
          label="Insurance Number"
          required={required}
          maxLength={16}
          value={entry.number}
          placeholder="EC2426456447-01E"
          error={errors.number}
          onChange={(event) =>
            onChange({ ...entry, number: normalizeNumber(event.target.value) })
          }
        />
        <DatePicker
          id={`${idPrefix}-insurance-from`}
          label="Valid From"
          required={required}
          selected={asDate(entry.from)}
          onChange={(date) => {
            const nextFrom = toDateOnly(date);
            onChange({
              ...entry,
              from: nextFrom,
              to: entry.to && entry.to < nextFrom ? "" : entry.to,
            });
          }}
          dateFormat="dd-MM-yyyy"
          placeholderText="Valid From"
          customInput={<DatePickerFieldTrigger />}
          error={errors.from}
          disabled={disabled}
        />
        <DatePicker
          id={`${idPrefix}-insurance-to`}
          label="Valid Till"
          selected={asDate(entry.to)}
          onChange={(date) => onChange({ ...entry, to: toDateOnly(date) })}
          minDate={asDate(entry.from) ?? undefined}
          maxDate={INSURANCE_VALID_TO_MAX_DATE}
          yearOrder="asc"
          dateFormat="dd-MM-yyyy"
          placeholderText="Valid Till"
          customInput={<DatePickerFieldTrigger />}
          error={errors.to}
          disabled={disabled || !entry.from}
          isClearable
        />
        <FileField
          id={`${idPrefix}-insurance-file`}
          label="Upload Insurance"
          accept={FILE_ACCEPT}
          stagedFile={entry.file}
          hint="PDF, JPG, JPEG, or PNG (maximum 5 MB)"
          error={errors.file}
          disabled={disabled}
          onChange={(file) => onChange({ ...entry, file })}
        />
      </div>
    </fieldset>
  );
}

export function EmployeeInsuranceTab({
  employeeId,
  maritalStatus,
  location,
  employeeType,
  insuranceDetails,
  loading = false,
  onInsuranceUpdated,
  onInsuranceSaved,
}: EmployeeInsuranceTabProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChildUnder18, setHasChildUnder18] = useState(
    Boolean(insuranceDetails?.childStatus),
  );
  const [form, setForm] = useState<InsuranceForm>({
    self: { ...EMPTY_ENTRY },
    spouse: { ...EMPTY_ENTRY },
    child: { ...EMPTY_ENTRY },
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [hospitals, setHospitals] = useState<HospitalDirectory>({});
  const [hospitalsOpen, setHospitalsOpen] = useState(false);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);
  const [hospitalsError, setHospitalsError] = useState("");
  const hospitalDropdownRef = useRef<HTMLDivElement | null>(null);
  const [loadingDocument, setLoadingDocument] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState("application/octet-stream");
  const [viewerName, setViewerName] = useState("Insurance document");

  const family = insuranceDetails?.familyInsuranceDetails ?? {};
  const spouse = getFamilyEntry(family, "spouse");
  const child = getFamilyEntry(family, "child");
  const insuranceId = insuranceDetails?.insuranceId ?? insuranceDetails?.id;
  const isMarried = String(
    maritalStatus ?? insuranceDetails?.maritalStatus ?? "",
  ).trim().toUpperCase() === "MARRIED";
  const showChildInsurance = isMarried && hasChildUnder18;
  const insuranceEligible = isInsuranceEligibleLocation(location, employeeType);

  useEffect(() => {
    setHasChildUnder18(Boolean(insuranceDetails?.childStatus));
  }, [insuranceDetails?.childStatus]);

  useEffect(() => {
    if (!insuranceEligible) {
      setHospitalsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setHospitalsLoading(true);
    setHospitalsError("");

    getApi<unknown>(
      API_HOSPITAL_LIST,
      getAuthToken("accessToken"),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const outer = unwrapRecord(data);
        const directory = unwrapRecord(outer.data ?? outer);
        setHospitals(
          Object.fromEntries(
            Object.entries(directory).filter(
              (entry): entry is [string, string[]] =>
                Array.isArray(entry[1]) && entry[1].every((item) => typeof item === "string"),
            ),
          ),
        );
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setHospitalsError("Unable to load network hospitals.");
      })
      .finally(() => setHospitalsLoading(false));

    return () => controller.abort();
  }, [insuranceEligible]);

  useEffect(() => {
    if (!insuranceEligible || !hospitalsOpen) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!hospitalDropdownRef.current?.contains(event.target as Node)) {
        setHospitalsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setHospitalsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [hospitalsOpen, insuranceEligible]);

  const closeViewer = useCallback(() => {
    setViewerUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  }, []);

  useEffect(() => closeViewer, [closeViewer]);

  useEffect(() => {
    if (!viewerUrl) return;
    const element = document.getElementById("staticBackdropDocumentViewer");
    if (element) {
      const modal = new window.bootstrap.Modal(element, { backdrop: false });
      modal.show();
    }
  }, [viewerUrl]);

  const openDocument = useCallback(async (path: string) => {
    setLoadingDocument(path);
    try {
      const url = `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(path)}`;
      const { data: blob } = await getApi<Blob>(
        url,
        getAuthToken("accessToken"),
        "blob",
      );
      const objectUrl = URL.createObjectURL(blob);
      setViewerUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return objectUrl;
      });
      setViewerType(blob.type || contentTypeFromPath(path));
      setViewerName(fileNameFromPath(path));
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to load insurance document.");
    } finally {
      setLoadingDocument(null);
    }
  }, []);
  

const summaryCards = useMemo(() => {
  const cards = [];

  if (insuranceDetails?.insuranceNumber) {
    cards.push({ title: "Self", data: insuranceDetails });
  }

  if (isMarried && (spouse.name || spouse.dob || spouse.insuranceNumber)) {
    cards.push({ title: "Spouse", data: spouse });
  }

  if (showChildInsurance && (child.name || child.dob || child.insuranceNumber)) {
    cards.push({ title: "Child", data: child });
  }

  return cards;
}, [child, insuranceDetails, isMarried, showChildInsurance, spouse]);
const hasInsurance = summaryCards.length > 0;

  const openEditor = () => {
    setForm({
      self: toEntryForm(insuranceDetails),
      spouse: toEntryForm(spouse),
      child: toEntryForm(child),
    });
    setErrors({});
    setEditing(true);
  };

  const updateEntry = (relation: keyof InsuranceForm, entry: InsuranceEntryForm) => {
    setForm((current) => ({ ...current, [relation]: entry }));
    setErrors((current) => ({ ...current, [relation]: {} }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: FormErrors = {
      self: validateEntry(form.self, "Self", true),
      ...(isMarried ? { spouse: validateEntry(form.spouse, "Spouse", true, "spouse") } : {}),
      ...(showChildInsurance ? { child: validateEntry(form.child, "Child", true, "child") } : {}),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some((group) => Object.keys(group ?? {}).length > 0)) return;
    if (employeeId == null || employeeId === "") {
      showError("Employee ID is missing.");
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("employeeId", String(employeeId));
      payload.append("insuranceProviderName", PROVIDER_NAME);
      appendEntry(payload, form.self, "");
      if (isMarried) appendEntry(payload, form.spouse, "spouse");
      if (showChildInsurance) appendEntry(payload, form.child, "child");

      const token = getAuthToken("accessToken");
      const response =
        insuranceId != null
          ? await putFormDataMultipart<unknown>(
              `${API_EMPLOYEE_INSURANCE}/${insuranceId}`,
              token,
              payload,
            )
          : await postFormDataMultipart<unknown>(
              API_EMPLOYEE_INSURANCE_EXISTING,
              token,
              payload,
            );

      const responseBody = unwrapRecord(response.data);
      const savedBody = unwrapRecord(responseBody.data ?? responseBody);
      const savedId =
        (savedBody.insuranceId as Identifier | undefined) ??
        (savedBody.id as Identifier | undefined) ??
        insuranceId;
      const updated: EmployeeInsuranceDetails = {
        ...insuranceDetails,
        ...(savedId != null ? { id: savedId, insuranceId: savedId } : {}),
        insuranceProviderName: PROVIDER_NAME,
        insuranceNumber: form.self.number,
        insuranceFrom: form.self.from,
        insuranceTo: form.self.to || null,
        maritalStatus,
        childStatus: showChildInsurance,
        familyInsuranceDetails: {
          spouseInsurance: isMarried
            ? {
                name: form.spouse.name,
                dob: form.spouse.dob,
                insuranceNumber: form.spouse.number,
                insuranceFrom: form.spouse.from,
                insuranceTo: form.spouse.to || null,
                insuranceSoftCopy: spouse.insuranceSoftCopy,
              }
            : null,
          childInsurance: showChildInsurance
            ? {
                name: form.child.name,
                dob: form.child.dob,
                gender: form.child.gender,
                insuranceNumber: form.child.number,
                insuranceFrom: form.child.from,
                insuranceTo: form.child.to || null,
                insuranceSoftCopy: child.insuranceSoftCopy,
              }
            : null,
        },
      };

      onInsuranceUpdated?.(updated);
      setEditing(false);
      onInsuranceSaved?.();
      showSuccess(
        insuranceId != null
          ? "Insurance details updated successfully."
          : "Insurance details uploaded successfully.",
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to save insurance details.");
    } finally {
      setSaving(false);
    }
  };

  if (!insuranceEligible) return null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2 className="heading-sm">Insurance Details</h2>
          <p className="body-sm">Manage employee and eligible dependent insurance records.</p>
        </div>
        {!editing && !loading ? (
          <Button startIcon={<Pencil size={16} />} onClick={openEditor}>
            {insuranceId != null ? "Update Insurance" : "Upload Insurance"}
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <Spinner />
          <span className="body-sm">Loading insurance details...</span>
        </div>
      ) : editing ? (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <TextField
            id="insurance-provider"
            label="Insurance Provider"
            value={PROVIDER_NAME}
            readOnly
          />
          <InsuranceEntryFields
            title="Self"
            idPrefix="self"
            entry={form.self}
            errors={errors.self}
            required
            disabled={saving}
            onChange={(entry) => updateEntry("self", entry)}
          />

          {isMarried ? (
            <InsuranceEntryFields
              title="Spouse"
              idPrefix="spouse"
              entry={form.spouse}
              errors={errors.spouse}
              required
              dependentType="spouse"
              disabled={saving}
              onChange={(entry) => updateEntry("spouse", entry)}
            />
          ) : null}

          {isMarried ? (
            <RadioGroup
              id="child-under-eighteen"
              name="childUnderEighteen"
              label="Do you have a child below 18 years of age?"
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
              value={hasChildUnder18 ? "yes" : "no"}
              onChange={(value) => setHasChildUnder18(value === "yes")}
              disabled={saving}
            />
          ) : null}

          {showChildInsurance ? (
            <InsuranceEntryFields
              title="Child"
              idPrefix="child"
              entry={form.child}
              errors={errors.child}
              required
              dependentType="child"
              disabled={saving}
              onChange={(entry) => updateEntry("child", entry)}
            />
          ) : null}

          <div className={styles.formActions}>
            <Button
              variant="light-grey"        
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
           
              disabled={saving}
            >
              {saving ? "Saving.." : "Save Insurance"}
            </Button>
          </div>
        </form>
      ) : (
  hasInsurance ? (
  <>
    <div className={styles.providerSummary}>
      <span className="body-sm">Insurance Provider</span>
      <span className="sub-heading-md">
        {insuranceDetails?.insuranceProviderName || PROVIDER_NAME}
      </span>
    </div>

    <div className={styles.summaryGrid}>
      {summaryCards.map(({ title, data }) => (
        <Card key={title} variant="subtle" className={styles.summaryCard}>
          <div className={styles.cardTitle}>
            <ShieldCheck size={18} aria-hidden />
            <h3 className="heading-sm">{title}</h3>
          </div>

          <dl className={styles.detailsList}>
            {title !== "Self" ? (
              <div>
                <dt className="body-sm">
                  {title === "Spouse" ? "Spouse Name" : "Child Name"}
                </dt>
                <dd className="sub-heading-md">{data?.name || "-"}</dd>
              </div>
            ) : null}

            {title !== "Self" ? (
              <div>
                <dt className="body-sm">Date of Birth</dt>
                <dd className="sub-heading-md">
                  {data?.dob ? formatCalendarDateDisplay(data.dob) : "-"}
                </dd>
              </div>
            ) : null}

            {title === "Child" ? (
              <div>
                <dt className="body-sm">Gender</dt>
                <dd className="sub-heading-md">
                  {genderLabel(data.gender)}
                </dd>
              </div>
            ) : null}

            <div>
              <dt className="body-sm">Insurance Number</dt>
              <dd className="sub-heading-md">{data?.insuranceNumber || "-"}</dd>
            </div>

            <div>
              <dt  className="body-sm">Valid From</dt>
              <dd className="sub-heading-md">{formatCalendarDateDisplay(data?.insuranceFrom)}</dd>
            </div>

            <div>
              <dt className="body-sm">Valid Till</dt>
              <dd className="sub-heading-md">{formatCalendarDateDisplay(data?.insuranceTo)}</dd>
            </div>

            <div>
              <dt className="body-sm">Uploaded Insurance</dt>
              <dd className="sub-heading-md">
                {data?.insuranceSoftCopy ? (
                  <Button
                    variant="link"
                    size="sm"
                    startIcon={<Eye size={15} aria-hidden />}
                    disabled={loadingDocument === data.insuranceSoftCopy}
                    onClick={() => void openDocument(data.insuranceSoftCopy as string)}
                  >
                    {loadingDocument === data.insuranceSoftCopy ? "Loading..." : "View"}
                  </Button>
                ) : (
                  "-"
                )}
              </dd>
            </div>
          </dl>
        </Card>
      ))}
    </div>
  </>
) : (
  <Card variant="subtle" className={styles.emptyState}>
    <ShieldCheck size={24} aria-hidden />
    <div>
      <h3 className="heading-xs">No insurance details uploaded</h3>
      <p className="body-sm">
        Use Upload Insurance to add this employee's policy details.
      </p>
    </div>
  </Card>
))}
  

{hasInsurance && (
  <section className={styles.hospitalSection}>
    <div className={styles.hospitalHeader}>
      <div className={styles.hospitalHeading}>
        <Building2 size={20} aria-hidden />
        <div>
          <h3 className="heading-xs">Network Hospitals</h3>
          <p className="body-sm">
            Hospitals covered under this insurance plan.
          </p>
        </div>
      </div>

          <div ref={hospitalDropdownRef} className={styles.hospitalActions}>
        <Button
          variant="muted-outline"
          size="sm"
          onClick={() => setHospitalsOpen((open) => !open)}
          aria-expanded={hospitalsOpen}
          aria-controls="insurance-hospital-list"
        >
          View covered hospitals
        </Button>

        {hospitalsOpen ? (
          <div
            id="insurance-hospital-list"
            className={styles.hospitalDropdown}
            role="region"
            aria-label="Hospitals covered under this plan"
          >
            <p className={styles.dropdownTitle}>
              Hospitals covered under this plan
            </p>

            {hospitalsLoading ? (
              <div className={styles.message}>
                <Spinner variant="inline" /> Loading hospitals
              </div>
            ) : hospitalsError ? (
              <p className={styles.errorMessage}>{hospitalsError}</p>
            ) : Object.keys(hospitals).length === 0 ? (
              <p className={styles.message}>
                No network hospitals are available.
              </p>
            ) : (
              <ul className={styles.hospitalList}>
                {Object.entries(hospitals).map(([city, cityHospitals]) => (
                  <li className={styles.cityGroup} key={city}>
                    <h4>{city}</h4>
                    <ul>
                      {cityHospitals.map((hospital) => (
                        <li key={`${city}-${hospital}`}>
                          <Building2 size={16} aria-hidden />
                          <span>{hospital}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </div>
  </section>
)}
      {viewerUrl ? (
        <DocumentViewer
          url={viewerUrl}
          contentType={viewerType}
          fileName={viewerName}
          onClose={closeViewer}
        />
      ) : null}
    </div>
  );
}

export default EmployeeInsuranceTab;
